function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function isHardwareDefinition(definition) {
  const category = String(definition?.category || "").toLowerCase();
  const nodeType = String(
    definition?.nodeType || definition?.node_type || ""
  ).toLowerCase();
  const geometryType = String(
    definition?.geometryType || definition?.geometry_type || ""
  ).toLowerCase();

  return (
    nodeType === "hardware" ||
    category.includes("hardware") ||
    geometryType === "catalog_glb"
  );
}

function getHardwareDefinition(definitionMap, definitionId) {
  if (!definitionId) return null;
  return definitionMap?.get?.(String(definitionId)) || null;
}

function getMounting(definition) {
  return definition?.linkedMounting || definition?.linked_mounting || null;
}

function getReferenceFrame(definition) {
  const mounting = getMounting(definition);

  return (
    mounting?.reference_frame_json || {
      origin: { x: 0, y: 0, z: 0 },
      offset: { x: 0, y: 0, z: 0 },
      defaultRotation: { x: 0, y: 0, z: 0 },
      leftRotation: { x: 0, y: 0, z: 0 },
      rightRotation: { x: 0, y: 180, z: 0 },
    }
  );
}

function getPlacementRules(definition) {
  const mounting = getMounting(definition);

  return (
    mounting?.placement_rules_json || {
      hardwareKind: "generic",
      handedness: "universal",
      mirrorAllowed: true,
      attach_points: [],
      components: [],
    }
  );
}

function addVec(a = {}, b = {}) {
  return {
    x: toNumber(a.x) + toNumber(b.x),
    y: toNumber(a.y) + toNumber(b.y),
    z: toNumber(a.z) + toNumber(b.z),
  };
}

function addRot(a = {}, b = {}) {
  return {
    x: toNumber(a.x) + toNumber(b.x),
    y: toNumber(a.y) + toNumber(b.y),
    z: toNumber(a.z) + toNumber(b.z),
  };
}

function applyMountingCorrection({
  position,
  rotation,
  hardwareDefinition,
  side = "default",
}) {
  if (!hardwareDefinition) {
    return { position, rotation };
  }

  const referenceFrame = getReferenceFrame(hardwareDefinition);

  // VAŽNO:
  // Offset i defaultRotation NE primenjujemo ovde,
  // jer se oni primenjuju u HierarchySceneCanvas kroz node.mountingCorrection.
  // Ovde dodajemo samo side-specific rotaciju za left/right varijante.
  const sideRotation =
    side === "right"
      ? referenceFrame.rightRotation || { x: 0, y: 180, z: 0 }
      : side === "left"
      ? referenceFrame.leftRotation || { x: 0, y: 0, z: 0 }
      : { x: 0, y: 0, z: 0 };

  return {
    position,
    rotation: addRot(rotation, sideRotation),
  };
}

function isDoorLikePanel(definition, effectiveParameters) {
  const category = String(definition?.category || "").toLowerCase();

  const behavior = String(
    effectiveParameters?.panelBehavior || ""
  ).toLowerCase();

  const hardwareMode = String(
    effectiveParameters?.hardwareMode || ""
  ).toLowerCase();

  if (behavior === "fixed") return false;
  if (hardwareMode === "none") return false;
  if (hardwareMode === "fixed") return false;

  return (
    category.startsWith("front.door") ||
    behavior === "door" ||
    hardwareMode === "hinged_left" ||
    hardwareMode === "hinged_right" ||
    hardwareMode === "lift"
  );
}

function getHingeSide(effectiveParameters) {
  const hardwareMode = String(
    effectiveParameters?.hardwareMode || ""
  ).toLowerCase();

  if (hardwareMode === "hinged_right") return "right";
  if (hardwareMode === "hinged_left") return "left";

  return String(effectiveParameters?.hingeSide || "left").toLowerCase();
}

function getHandleSide(effectiveParameters, hingeSide) {
  const handleSide = String(
    effectiveParameters?.handleSide || "opposite_hinge"
  ).toLowerCase();

  if (handleSide === "opposite_hinge") {
    return hingeSide === "left" ? "right" : "left";
  }

  return handleSide;
}

function createHardwareChild({
  id,
  definitionId,
  role,
  position,
  rotation,
  parameterOverrides = {},
}) {
  if (!definitionId) return null;

  return {
    id,
    definitionId,
    role,
    parameterBindings: {},
    parameterOverrides,
    transform: {
      position,
      rotation,
    },
    children: [],
  };
}

function generateHinges({ instanceNode, effectiveParameters, definitionMap }) {
  const hingeDefinitionId = effectiveParameters?.hingeDefinitionId;
  if (!hingeDefinitionId) return [];

  const hardwareDefinition = getHardwareDefinition(
    definitionMap,
    hingeDefinitionId
  );

  const width = toNumber(effectiveParameters.width, 0);
  const height = toNumber(effectiveParameters.height, 0);
  const depth = toNumber(
    effectiveParameters.depth,
    toNumber(effectiveParameters.thickness, 18)
  );

  const hingeSide = getHingeSide(effectiveParameters);
  const hingeCount = Math.max(1, toNumber(effectiveParameters.hingeCount, 2));

  const topOffset = toNumber(effectiveParameters.hingeTopOffset, 100);
  const bottomOffset = toNumber(effectiveParameters.hingeBottomOffset, 100);

 const panelThickness = toNumber(
  effectiveParameters.thickness,
  toNumber(effectiveParameters.depth, 18)
);

const frontGapOuter = toNumber(effectiveParameters.frontGapOuter, 0);
const frontGapInner = toNumber(effectiveParameters.frontGapInner, frontGapOuter);

const defaultHingeOffsetFromEdge = Math.max(
  0,
  panelThickness - frontGapOuter
);

const hingeOffsetFromEdge = toNumber(
  effectiveParameters.hingeOffsetFromEdge,
  defaultHingeOffsetFromEdge
);

const hingeDepthFromBackEdge = toNumber(
  effectiveParameters.hingeDepthFromBackEdge,
  37
);

const hingeOffsetZ = toNumber(
  effectiveParameters.hingeOffsetZ,
  -depth / 2 - hingeDepthFromBackEdge
);

const x =
  hingeSide === "left"
    ? -width / 2 + hingeOffsetFromEdge
    : width / 2 - hingeOffsetFromEdge;

const z = hingeOffsetZ;

 const baseRotation = { x: 0, y: 0, z: 0 };

  const result = [];

  for (let i = 0; i < hingeCount; i += 1) {
    let y = 0;

    if (hingeCount === 1) {
      y = 0;
    } else if (i === 0) {
      y = height / 2 - topOffset;
    } else if (i === hingeCount - 1) {
      y = -height / 2 + bottomOffset;
    } else {
      const usable = height - topOffset - bottomOffset;
      y = height / 2 - topOffset - (usable / (hingeCount - 1)) * i;
    }

    const corrected = applyMountingCorrection({
      position: {
        x: Number(x.toFixed(3)),
        y: Number(y.toFixed(3)),
        z: Number(z.toFixed(3)),
      },
      rotation: baseRotation,
      hardwareDefinition,
      side: hingeSide,
    });

    const child = createHardwareChild({
      id: `generated_hinge_${i + 1}_${instanceNode.id}`,
      definitionId: hingeDefinitionId,
      role: "hinge",
      position: corrected.position,
      rotation: corrected.rotation,
      parameterOverrides: {
  hardwareSide: hingeSide,
  hardwareIndex: i + 1,

  panelThickness,
  frontGapOuter,
  frontGapInner,
  hingeOffsetFromEdge,
  hingeDepthFromBackEdge,
  hingeOffsetZ,
},
    });

    if (child) result.push(child);
  }

  return result;
}

function hasValue(value) {
  return value !== undefined && value !== null && value !== "";
}

function generateHandle({ instanceNode, effectiveParameters, definitionMap }) {
  const handleDefinitionId = effectiveParameters?.handleDefinitionId;
  const handleEnabled = toNumber(effectiveParameters.handleEnabled, 0);

  if (!handleDefinitionId || !handleEnabled) return [];

  const hardwareDefinition = getHardwareDefinition(
    definitionMap,
    handleDefinitionId
  );

  const width = toNumber(effectiveParameters.width, 0);
  const depth = toNumber(
    effectiveParameters.depth,
    toNumber(effectiveParameters.thickness, 18)
  );

  const panelBehavior = String(
    effectiveParameters.panelBehavior || ""
  ).toLowerCase();

  const hardwareMode = String(
    effectiveParameters.hardwareMode || ""
  ).toLowerCase();

  const handlePlacementMode = String(
    effectiveParameters.handlePlacementMode || "auto"
  ).toLowerCase();

  const isDrawer =
    panelBehavior === "drawer" || hardwareMode === "drawer";

  const isDoor =
    panelBehavior === "door" ||
    hardwareMode === "hinged_left" ||
    hardwareMode === "hinged_right" ||
    hardwareMode === "lift";

  const hingeSide = getHingeSide(effectiveParameters);
  const handleSide = getHandleSide(effectiveParameters, hingeSide);

  // Default:
  // - drawer: horizontal, centered
  // - door: vertical, near opposite hinge side
  const autoRotation = isDoor
    ? { x: 0, y: 0, z: 90 }
    : { x: 0, y: 0, z: 0 };

  const handleRotation = {
    x:
      handlePlacementMode === "custom" && hasValue(effectiveParameters.handleRotationX)
        ? toNumber(effectiveParameters.handleRotationX, autoRotation.x)
        : autoRotation.x,

    y:
      handlePlacementMode === "custom" && hasValue(effectiveParameters.handleRotationY)
        ? toNumber(effectiveParameters.handleRotationY, autoRotation.y)
        : autoRotation.y,

    z:
      handlePlacementMode === "custom" && hasValue(effectiveParameters.handleRotationZ)
        ? toNumber(effectiveParameters.handleRotationZ, autoRotation.z)
        : autoRotation.z,
  };

  const defaultHandleOffsetX = isDrawer ? 0 : 40;
  const defaultHandleOffsetY = 0;
  const defaultHandleOffsetZ = 10;

  const handleOffsetX = hasValue(effectiveParameters.handleOffsetX)
    ? toNumber(effectiveParameters.handleOffsetX, defaultHandleOffsetX)
    : defaultHandleOffsetX;

  const handleOffsetY = hasValue(effectiveParameters.handleOffsetY)
    ? toNumber(effectiveParameters.handleOffsetY, defaultHandleOffsetY)
    : defaultHandleOffsetY;

  const handleOffsetZ = hasValue(effectiveParameters.handleOffsetZ)
    ? toNumber(effectiveParameters.handleOffsetZ, defaultHandleOffsetZ)
    : defaultHandleOffsetZ;

  // Drawer: X offset je od centra panela.
  // Door: X offset je od bočne ivice / strane ručke.
  const x = isDrawer
    ? handleOffsetX
    : handleSide === "left"
    ? -width / 2 + handleOffsetX
    : width / 2 - handleOffsetX;

  const y = handleOffsetY;
  const z = depth / 2 + handleOffsetZ;

  const corrected = applyMountingCorrection({
    position: {
      x: Number(x.toFixed(3)),
      y: Number(y.toFixed(3)),
      z: Number(z.toFixed(3)),
    },
    rotation: handleRotation,
    hardwareDefinition,
    side: handleSide,
  });

  const child = createHardwareChild({
    id: `generated_handle_${instanceNode.id}`,
    definitionId: handleDefinitionId,
    role: "handle",
    position: corrected.position,
    rotation: corrected.rotation,
    parameterOverrides: {
      hardwareSide: handleSide,

      panelBehavior,
      hardwareMode,
      handlePlacementMode,

      isDrawer,
      isDoor,

      handleOffsetX,
      handleOffsetY,
      handleOffsetZ,

      handleRotationX: handleRotation.x,
      handleRotationY: handleRotation.y,
      handleRotationZ: handleRotation.z,
    },
  });

  return child ? [child] : [];
}

function generateLiftPlaceholders({
  instanceNode,
  effectiveParameters,
  definitionMap,
}) {
  const liftDefinitionId = effectiveParameters?.liftDefinitionId;
  if (!liftDefinitionId) return [];

  const hardwareMode = String(
    effectiveParameters?.hardwareMode || ""
  ).toLowerCase();

  if (hardwareMode !== "lift") return [];

  const hardwareDefinition = getHardwareDefinition(
    definitionMap,
    liftDefinitionId
  );

  const width = toNumber(effectiveParameters.width, 0);
  const height = toNumber(effectiveParameters.height, 0);
  const depth = toNumber(
    effectiveParameters.depth,
    toNumber(effectiveParameters.thickness, 18)
  );

  const liftOffsetX = toNumber(effectiveParameters.liftOffsetX, 60);
  const liftOffsetY = toNumber(effectiveParameters.liftOffsetY, 120);
  const liftOffsetZ = toNumber(effectiveParameters.liftOffsetZ, 0);

  const y = height / 2 - liftOffsetY;
  const z = -depth / 2 + liftOffsetZ;

  const leftCorrected = applyMountingCorrection({
    position: {
      x: Number((-width / 2 + liftOffsetX).toFixed(3)),
      y: Number(y.toFixed(3)),
      z: Number(z.toFixed(3)),
    },
    rotation: { x: 0, y: 0, z: 0 },
    hardwareDefinition,
    side: "left",
  });

  const rightCorrected = applyMountingCorrection({
    position: {
      x: Number((width / 2 - liftOffsetX).toFixed(3)),
      y: Number(y.toFixed(3)),
      z: Number(z.toFixed(3)),
    },
    rotation: { x: 0, y: 0, z: 0 },
    hardwareDefinition,
    side: "right",
  });

  const left = createHardwareChild({
    id: `generated_lift_left_${instanceNode.id}`,
    definitionId: liftDefinitionId,
    role: "lift_left",
    position: leftCorrected.position,
    rotation: leftCorrected.rotation,
    parameterOverrides: {
      hardwareSide: "left",
    },
  });

  const right = createHardwareChild({
    id: `generated_lift_right_${instanceNode.id}`,
    definitionId: liftDefinitionId,
    role: "lift_right",
    position: rightCorrected.position,
    rotation: rightCorrected.rotation,
    parameterOverrides: {
      hardwareSide: "right",
    },
  });

  return [left, right].filter(Boolean);
}

function isDrawerPanel(effectiveParameters) {
  const behavior = String(
    effectiveParameters?.panelBehavior || ""
  ).toLowerCase();

  const hardwareMode = String(
    effectiveParameters?.hardwareMode || ""
  ).toLowerCase();

  return behavior === "drawer" || hardwareMode === "drawer";
}

function findDefinitionByCategory(definitionMap, category) {
  if (!definitionMap) return null;

  for (const def of definitionMap.values()) {
    if (String(def?.category || "") === String(category)) {
      return def;
    }
  }

  return null;
}

function findDrawerBoardDefinitionId(definitionMap) {
  return (
    findDefinitionByCategory(definitionMap, "board.panel")?.id ||
    findDefinitionByCategory(definitionMap, "board.side")?.id ||
    findDefinitionByCategory(definitionMap, "board.horizontal")?.id ||
    null
  );
}

function generateDrawerRunner({
  instanceNode,
  effectiveParameters,
}) {
  const runnerDefinitionId = effectiveParameters?.drawerRunnerDefinitionId;
  if (!runnerDefinitionId) return [];

  const width = toNumber(effectiveParameters.width, 0);
  const height = toNumber(effectiveParameters.height, 0);

  const frontThickness = toNumber(
    effectiveParameters.depth,
    toNumber(effectiveParameters.thickness, 18)
  );

  const drawerOffsetLeft = toNumber(effectiveParameters.drawerOffsetLeft, 16);
  const drawerOffsetRight = toNumber(effectiveParameters.drawerOffsetRight, 16);
  const drawerOffsetBottom = toNumber(effectiveParameters.drawerOffsetBottom, 5);
  const drawerOffsetTop = toNumber(effectiveParameters.drawerOffsetTop, 20);

  const runnerLength = toNumber(effectiveParameters.runnerLength, 300);
  const runnerHeight = toNumber(effectiveParameters.runnerHeight, 40);
  const drawerDepth = toNumber( effectiveParameters.drawerDepth, runnerLength - 10);

  const sideClearance = toNumber(effectiveParameters.sideClearance, 7.5);

  // Klizač počinje iza dekor/front panela.
// Ne dodajemo drawerFrontBackThickness ovde.
const runnerOriginZ = -frontThickness / 2;

const runnerSpanWidth = Math.max(
  1,
  width - drawerOffsetLeft - drawerOffsetRight
);

const drawerBoxWidth = Math.max(
  1,
  runnerSpanWidth - 2 * sideClearance
);

  const drawerBoxHeight = Math.max(
    1,
    height - drawerOffsetBottom - drawerOffsetTop
  );

  const drawerBoxY = (drawerOffsetBottom - drawerOffsetTop) / 2;

  return [
    {
      id: `generated_drawer_runner_${instanceNode.id}`,
      definitionId: runnerDefinitionId,
      role: "drawer_runner",

      parameterBindings: {},

      parameterOverrides: {
  // širina prostora u kome se pozicioniraju levi/desni klizač
  cabinetInnerWidth: runnerSpanWidth,
  runnerSpanWidth,

  // stvarna širina fioke između klizača
  drawerWidth: drawerBoxWidth,
  drawerBoxWidth,

  frontThickness,
runnerOriginZ,

  drawerBoxHeight,
  drawerDepth,

  drawerOffsetLeft,
  drawerOffsetRight,
  drawerOffsetBottom,
  drawerOffsetTop,

  sideClearance,
  runnerHeight,
  runnerLength,
},

      transform: {
  position: {
    x: (drawerOffsetLeft - drawerOffsetRight) / 2,
    y: drawerBoxY,
    z: runnerOriginZ,
  },
  rotation: {
    x: 0,
    y: 0,
    z: 0,
  },
},

      children: [],
    },
  ];
}

function generateDrawerBoxBoards({
  instanceNode,
  effectiveParameters,
  definitionMap,
}) {
  const boardDefinitionId =
    effectiveParameters?.drawerBoardDefinitionId ||
    findDrawerBoardDefinitionId(definitionMap);

  if (!boardDefinitionId) return [];

  const width = toNumber(effectiveParameters.width, 0);
  const height = toNumber(effectiveParameters.height, 0);

  const frontThickness = toNumber(
    effectiveParameters.depth,
    toNumber(effectiveParameters.thickness, 18)
  );

  const drawerOffsetLeft = toNumber(effectiveParameters.drawerOffsetLeft, 16);
  const drawerOffsetRight = toNumber(effectiveParameters.drawerOffsetRight, 16);
  const sideClearance = toNumber(effectiveParameters.sideClearance, 7.5);
  const drawerOffsetBottom = toNumber(effectiveParameters.drawerOffsetBottom, 40);
  const drawerOffsetTop = toNumber(effectiveParameters.drawerOffsetTop, 20);
  

  const runnerLength = toNumber(effectiveParameters.runnerLength, 300);
  const runnerHeight = toNumber(effectiveParameters.runnerHeight, 40);

  const drawerSideThickness = toNumber(
    effectiveParameters.drawerSideThickness,
    16
  );

  const drawerBottomThickness = toNumber(
    effectiveParameters.drawerBottomThickness,
    18
  );

  const drawerBottomOffset = toNumber(
    effectiveParameters.drawerBottomOffset,
    10
  );

  const drawerSideDropBelowRunner = toNumber(
  effectiveParameters.drawerSideDropBelowRunner,
  10
);

  const drawerFrontBackThickness = toNumber(
    effectiveParameters.drawerFrontBackThickness,
    drawerSideThickness
  );

  const runnerSpanWidth = Math.max(
  1,
  width - drawerOffsetLeft - drawerOffsetRight
);

const drawerBoxWidth = Math.max(
  1,
  runnerSpanWidth - 2 * sideClearance
);

  const drawerBoxHeight = Math.max(
    1,
    height - drawerOffsetBottom - drawerOffsetTop
  );

  const drawerDepth = toNumber(
  effectiveParameters.drawerDepth,
  runnerLength - 10
);

  const drawerBoxX = (drawerOffsetLeft - drawerOffsetRight) / 2;
  const drawerBoxY = (drawerOffsetBottom - drawerOffsetTop) / 2;

  // Z referenca: zadnja strana fronta je početak fioke
  const frontBackZ = -frontThickness / 2;
  const drawerCenterZ = frontBackZ - drawerDepth / 2;

 const innerWidth = Math.max(1, drawerBoxWidth - 2 * drawerSideThickness);

// Gornja granica drawer box-a je vezana za front panel:
// height - bottom offset - top offset
const desiredTopY = drawerBoxY + drawerBoxHeight / 2;

// Linija klizača u lokalnom sistemu drawer/front panela.
// runnerHeight znači visina klizača od donje referentne zone.
const runnerLineY =
  drawerBoxY - drawerBoxHeight / 2 + runnerHeight;

// Donja ivica bočne stranice prati klizač,
// npr. 10 mm ispod linije klizača.
const sideBottomY = runnerLineY - drawerSideDropBelowRunner;

// Stvarna visina bočne stranice ne sme da pređe gornju granicu.
const sideHeight = Math.max(1, desiredTopY - sideBottomY);

// Centar bočne stranice.
const sideCenterY = sideBottomY + sideHeight / 2;

// Donja daska ide unutar drawer box-a,
// offsetovana od donje ivice bočnih stranica.
const bottomY =
  sideBottomY +
  drawerBottomOffset +
  drawerBottomThickness / 2;

// Prednja i zadnja unutrašnja stranica idu od gornje strane dna do vrha fioke.
const verticalBoardHeight = Math.max(
  1,
  sideHeight - drawerBottomOffset - drawerBottomThickness
);

const verticalBoardY =
  sideBottomY +
  drawerBottomOffset +
  drawerBottomThickness +
  verticalBoardHeight / 2;

const commonMaterialRole = "drawer_box";

  return [
    // LEFT SIDE
    {
  id: `generated_drawer_side_left_${instanceNode.id}`,
  definitionId: boardDefinitionId,
  role: "drawer_side_left",
  parameterBindings: {},
  parameterOverrides: {
    panelBehavior: "fixed",
    hardwareMode: "none",

    width: drawerDepth,
    height: sideHeight,
    depth: drawerSideThickness,
    thickness: drawerSideThickness,
  },
  transform: {
    position: {
      x: drawerBoxX - drawerBoxWidth / 2 + drawerSideThickness / 2,
      y: sideCenterY,
      z: drawerCenterZ,
    },
    rotation: { x: 0, y: -90, z: 0 },
  },
  materialRole: commonMaterialRole,
  children: [],
},

    // RIGHT SIDE
    {
  id: `generated_drawer_side_right_${instanceNode.id}`,
  definitionId: boardDefinitionId,
  role: "drawer_side_right",
  parameterBindings: {},
  parameterOverrides: {
    panelBehavior: "fixed",
    hardwareMode: "none",

    width: drawerDepth,
    height: sideHeight,
    depth: drawerSideThickness,
    thickness: drawerSideThickness,
  },
  transform: {
    position: {
      x: drawerBoxX + drawerBoxWidth / 2 - drawerSideThickness / 2,
      y: sideCenterY,
      z: drawerCenterZ,
    },
    rotation: { x: 0, y: 90, z: 0 },
  },
  materialRole: commonMaterialRole,
  children: [],
},

    // BOTTOM BOARD
    {
  id: `generated_drawer_bottom_${instanceNode.id}`,
  definitionId: boardDefinitionId,
  role: "drawer_bottom",
  parameterBindings: {},
  parameterOverrides: {
    panelBehavior: "fixed",
    hardwareMode: "none",

    width: innerWidth,
    height: drawerDepth,
    depth: drawerBottomThickness,
    thickness: drawerBottomThickness,
  },
  transform: {
    position: {
      x: drawerBoxX,
      y: bottomY,
      z: drawerCenterZ,
    },
    rotation: { x: 90, y: 0, z: 0 },
  },
  materialRole: commonMaterialRole,
  children: [],
},

    // FRONT INNER WALL
    {
  id: `generated_drawer_inner_front_${instanceNode.id}`,
  definitionId: boardDefinitionId,
  role: "drawer_inner_front",
  parameterBindings: {},
  parameterOverrides: {
    panelBehavior: "fixed",
    hardwareMode: "none",

    width: innerWidth,
    height: verticalBoardHeight,
    depth: drawerFrontBackThickness,
    thickness: drawerFrontBackThickness,
  },
  transform: {
    position: {
      x: drawerBoxX,
      y: verticalBoardY,
      z: frontBackZ - drawerFrontBackThickness / 2,
    },
    rotation: { x: 0, y: 0, z: 0 },
  },
  materialRole: commonMaterialRole,
  children: [],
},

    // BACK WALL
    {
  id: `generated_drawer_back_${instanceNode.id}`,
  definitionId: boardDefinitionId,
  role: "drawer_back",
  parameterBindings: {},
  parameterOverrides: {
    panelBehavior: "fixed",
    hardwareMode: "none",

    width: innerWidth,
    height: verticalBoardHeight,
    depth: drawerFrontBackThickness,
    thickness: drawerFrontBackThickness,
  },
  transform: {
    position: {
      x: drawerBoxX,
      y: verticalBoardY,
      z: frontBackZ - drawerDepth + drawerFrontBackThickness / 2,
    },
    rotation: { x: 0, y: 180, z: 0 },
  },
  materialRole: commonMaterialRole,
  children: [],
},
  ];
}

function round3(value) {
  return Number(toNumber(value, 0).toFixed(3));
}

function getHardwareDedupeKey(child) {
  const p = child?.transform?.position || {};
  const r = child?.transform?.rotation || {};

  return [
    child?.definitionId || "",
    child?.role || "",
    round3(p.x),
    round3(p.y),
    round3(p.z),
    round3(r.x),
    round3(r.y),
    round3(r.z),
  ].join("|");
}

function dedupeHardwareChildren(children = []) {
  const seen = new Set();

  return children.filter((child) => {
    const key = getHardwareDedupeKey(child);

    if (seen.has(key)) {
      console.warn("Duplicate generated hardware removed:", {
        key,
        id: child.id,
        role: child.role,
        definitionId: child.definitionId,
        position: child.transform?.position,
      });

      return false;
    }

    seen.add(key);
    return true;
  });
}

export function resolvePanelHardwareChildren({
  instanceNode,
  definition,
  effectiveParameters,
  definitionMap,
}) {
  // Hardware nodes must not generate more hardware children.
  if (isHardwareDefinition(definition)) {
    return [];
  }

  // Drawer-generated boards must not generate drawer internals again.
  const role = String(instanceNode?.role || "").toLowerCase();
  const id = String(instanceNode?.id || "").toLowerCase();

  const isGeneratedDrawerPart =
    role.startsWith("drawer_") ||
    id.includes("generated_drawer_side") ||
    id.includes("generated_drawer_bottom") ||
    id.includes("generated_drawer_inner_front") ||
    id.includes("generated_drawer_back");

  if (isGeneratedDrawerPart) {
    return [];
  }

  const drawerChildren = isDrawerPanel(effectiveParameters)
    ? [
        ...generateDrawerBoxBoards({
          instanceNode,
          effectiveParameters,
          definitionMap,
        }),
        ...generateDrawerRunner({
          instanceNode,
          effectiveParameters,
          definitionMap,
        }),
      ]
    : [];

  const doorChildren = isDoorLikePanel(definition, effectiveParameters)
    ? [
        ...generateHinges({
          instanceNode,
          definition,
          effectiveParameters,
          definitionMap,
        }),
        ...generateHandle({
          instanceNode,
          effectiveParameters,
          definitionMap,
        }),
        ...generateLiftPlaceholders({
          instanceNode,
          effectiveParameters,
          definitionMap,
        }),
      ]
    : [];

  return dedupeHardwareChildren([...doorChildren, ...drawerChildren]);
}